import { Injectable } from '@angular/core';
import { ChildProfileResponseDto } from '../models/api-types';

export interface ActiveChildProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * SessionStorage keys owned by `ActiveChildProfileService`. Exposed so that
 * session-lifecycle code (e.g. `AuthService.clearSession`) can wipe them on
 * logout without spreading magic strings across the codebase. The write paths
 * are dead today (see `docs/impersonation.md` §6.3); the cleanup guards
 * against future revival of the flow leaking across sessions.
 */
export const ACTIVE_CHILD_PROFILE_STORAGE_KEYS: readonly string[] = [
  'ui:activeChildProfileId',
  'ui:activeChildProfileEmail',
  'ui:activeChildProfileFirstName',
  'ui:activeChildProfileLastName',
] as const;

@Injectable({ providedIn: 'root' })
export class ActiveChildProfileService {
  private readonly storageKey = ACTIVE_CHILD_PROFILE_STORAGE_KEYS[0];
  private readonly emailStorageKey = ACTIVE_CHILD_PROFILE_STORAGE_KEYS[1];
  private readonly firstNameStorageKey = ACTIVE_CHILD_PROFILE_STORAGE_KEYS[2];
  private readonly lastNameStorageKey = ACTIVE_CHILD_PROFILE_STORAGE_KEYS[3];
  private activeProfile: ActiveChildProfile | null = null;

  constructor() {
    this.activeProfile = this.readStoredProfile();
  }

  getActiveChildProfile(): ActiveChildProfile | null {
    return this.activeProfile;
  }

  setActiveChildProfile(profile: ChildProfileResponseDto | null): void {
    if (!profile) {
      this.activeProfile = null;
      this.clearStorage();
      return;
    }
    this.activeProfile = {
      id: profile.id,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
    };
    this.persistProfile(this.activeProfile);
  }

  clearActiveChildProfile(): void {
    this.activeProfile = null;
    this.clearStorage();
  }

  isActingAsChild(): boolean {
    return this.activeProfile !== null;
  }

  private readStoredProfile(): ActiveChildProfile | null {
    try {
      const id = sessionStorage.getItem(this.storageKey);
      const email = sessionStorage.getItem(this.emailStorageKey);
      if (id && email) {
        return {
          id,
          email,
          firstName: sessionStorage.getItem(this.firstNameStorageKey) ?? '',
          lastName: sessionStorage.getItem(this.lastNameStorageKey) ?? '',
        };
      }
    } catch {
      // Ignore storage errors in private/restricted browsing contexts.
    }
    return null;
  }

  private persistProfile(profile: ActiveChildProfile): void {
    try {
      sessionStorage.setItem(this.storageKey, profile.id);
      sessionStorage.setItem(this.emailStorageKey, profile.email);
      sessionStorage.setItem(this.firstNameStorageKey, profile.firstName ?? '');
      sessionStorage.setItem(this.lastNameStorageKey, profile.lastName ?? '');
    } catch {
      // Ignore storage errors in private/restricted browsing contexts.
    }
  }

  private clearStorage(): void {
    try {
      sessionStorage.removeItem(this.storageKey);
      sessionStorage.removeItem(this.emailStorageKey);
      sessionStorage.removeItem(this.firstNameStorageKey);
      sessionStorage.removeItem(this.lastNameStorageKey);
    } catch {
      // Ignore storage errors in private/restricted browsing contexts.
    }
  }
}
